/**
 * TemplateParamsEditor: editor ÚNICO das variáveis de um template da Meta
 * (2026-09-14), usado por todas as telas que escolhem template.
 *
 * - Mostra a frase do corpo com cada {{n}} destacado no lugar em que aparece.
 * - Para cada variável do corpo e do cabeçalho de texto pergunta "O que entra
 *   aqui?" (Primeiro nome, Nome completo, Saudação, Responsável, Telefone,
 *   Data, Hora, Campo personalizado ou Texto fixo).
 * - Cabeçalho de mídia por link público ou arquivo enviado; botão de link
 *   variável e de copiar código com campo próprio, que aceita as mesmas opções.
 * - Grava exatamente o que o envio já entende (utils/templateParams.js).
 * - Prévia com um contato real quando há um (o do inbox, ou um de exemplo do
 *   público); sem contato, a prévia mostra o nome de cada escolha.
 * - Diz em linguagem simples o que acontece com quem não tem o dado.
 *
 * value/onChange: { body_params, header_param, button_params }.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Paperclip, AlertTriangle, Info, Link2 } from 'lucide-react';
import customFieldsService from '../../services/global/customFieldsService';
import fileUploadService from '../../services/global/fileUploadService';
import templateService from '../../services/global/templateService';
import {
  analyzeTemplate, normalizeValues, parseValue, serializeChoice, describeValue,
  labelPreview, buildSendExtra, missingDataSentence, previewMissingSentences,
  VALUE_SOURCES, MEDIA_LABEL, CAPS_FULL, bodyTokenLabel,
} from '../../utils/templateParams';

// Campos personalizados: uma leitura por sessão, compartilhada entre editores.
let fieldsPromise = null;
export const loadCustomFields = () => {
  if (!fieldsPromise) {
    fieldsPromise = Promise.resolve()
      .then(() => customFieldsService.getSchema())
      .then((schema) => {
        const defs = (schema?.definitions && schema.definitions.length)
          ? schema.definitions
          : (schema?.tabs || []).flatMap((t) => t.definitions || []);
        return defs.filter((d) => d && d.name);
      })
      .catch(() => {
        fieldsPromise = null;
        return [];
      });
  }
  return fieldsPromise;
};

const MEDIA_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

const looksOpaque = (key) => String(key || '').includes(':');

/** "O que entra aqui?" + o campo ou o texto, conforme a escolha. */
const ValueSourceField = ({ raw, onChange, fields, colors, inputStyle, maxLength }) => {
  const parsed = useMemo(() => parseValue(raw, fields), [raw, fields]);
  // Escolha feita sem valor ainda (campo sem nome escolhido, texto vazio).
  const [pending, setPending] = useState('');
  const source = parsed.source || pending;

  const handleSource = (key) => {
    setPending(key);
    if (key === 'fixo') onChange(parsed.source === 'fixo' ? raw : '');
    else if (key === 'campo' || !key) onChange('');
    else onChange(serializeChoice({ source: key }));
  };

  const campoAtual = parsed.source === 'campo' ? parsed.field : '';
  const campoForaDaLista = campoAtual && !fields.some((f) => f.name === campoAtual);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <select
        aria-label="O que entra aqui?"
        value={source}
        onChange={(e) => handleSource(e.target.value)}
        style={inputStyle}
      >
        <option value="">O que entra aqui?</option>
        {VALUE_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
      {source === 'campo' && (
        <select
          aria-label="Campo personalizado"
          value={campoAtual}
          onChange={(e) => onChange(serializeChoice({ source: 'campo', field: e.target.value }))}
          style={inputStyle}
        >
          <option value="">Escolha o campo</option>
          {fields.map((f) => <option key={f.id || f.name} value={f.name}>{f.name}</option>)}
          {campoForaDaLista && <option value={campoAtual}>{campoAtual}</option>}
        </select>
      )}
      {source === 'campo' && parsed.fieldMissing && (
        <span style={{ fontSize: 11, color: '#b45309' }}>
          O campo {campoAtual} não existe mais nesta conta: escolha outro.
        </span>
      )}
      {source === 'fixo' && (
        <input
          aria-label="Texto fixo"
          value={parsed.source === 'fixo' ? parsed.text : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Digite o texto"
          maxLength={maxLength}
          style={inputStyle}
        />
      )}
    </div>
  );
};

/** Cabeçalho de mídia: link público ou arquivo enviado. */
const MediaField = ({ format, value, onChange, colors, inputStyle }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const tipo = String(format || '').toLowerCase();
  const link = value?.link || '';

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const up = await fileUploadService.uploadFile(file, null, { channel: 'wa_business' });
      onChange({
        type: tipo, link: up.url,
        ...(format === 'DOCUMENT' && up.filename ? { filename: up.filename } : {}),
      });
    } catch (err) {
      setError(err?.message || 'Não foi possível enviar o arquivo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
          <Link2 size={13} color={colors.textTertiary} style={{ position: 'absolute', left: 9, top: 11 }} />
          <input
            aria-label={`Link ${MEDIA_LABEL[format] || 'da mídia'}`}
            value={link}
            onChange={(e) => onChange({ ...(value || {}), type: tipo, link: e.target.value })}
            placeholder="https://..."
            style={{ ...inputStyle, width: '100%', paddingLeft: 28 }}
          />
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            ...inputStyle, display: 'inline-flex', alignItems: 'center', gap: 6,
            cursor: uploading ? 'wait' : 'pointer', color: colors.textSecondary, flex: '0 0 auto',
          }}
        >
          {uploading
            ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <Paperclip size={13} />}
          {uploading ? 'Enviando...' : 'Enviar arquivo'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={MEDIA_ACCEPT[format] || ''}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            handleFile(file);
          }}
        />
      </div>
      {value?.filename && <span style={{ fontSize: 11, color: colors.textTertiary }}>{value.filename}</span>}
      {error && <span style={{ fontSize: 11, color: colors.danger || '#ef4444' }}>{error}</span>}
    </div>
  );
};

const Row = ({ label, children, colors }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <span style={{ fontSize: 11.5, fontWeight: 600, color: colors.textSecondary }}>{label}</span>
    {children}
  </div>
);

const PreviewBubble = ({ preview, colors }) => {
  if (!preview) return null;
  const { header, body_text: bodyText, footer_text: footer, buttons } = preview;
  return (
    <div style={{
      borderRadius: 10, padding: '10px 12px', maxWidth: 360,
      backgroundColor: colors.bgSecondary, border: `1px solid ${colors.borderColor}`,
    }}>
      {header && header.format !== 'TEXT' && (
        header.media_link && header.format === 'IMAGE' ? (
          <img
            src={header.media_link}
            alt="Imagem do cabeçalho"
            style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 6 }}
          />
        ) : (
          <div style={{
            fontSize: 11, color: colors.textTertiary, padding: '10px 0', marginBottom: 6,
            textAlign: 'center', border: `1px dashed ${colors.borderColor}`, borderRadius: 8,
          }}>
            {MEDIA_LABEL[header.format] || 'Mídia'}{header.media_link ? '' : ' do cabeçalho'}
          </div>
        )
      )}
      {header && header.format === 'TEXT' && header.text && (
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, marginBottom: 4 }}>
          {header.text}
        </div>
      )}
      <div style={{ fontSize: 13, color: colors.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
        {bodyText}
      </div>
      {footer && (
        <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 6 }}>{footer}</div>
      )}
      {(buttons || []).length > 0 && (
        <div style={{ marginTop: 8, borderTop: `1px solid ${colors.borderColor}` }}>
          {buttons.map((b) => (
            <div
              key={b.index}
              title={b.url || ''}
              style={{
                textAlign: 'center', padding: '7px 0', fontSize: 13, fontWeight: 600,
                color: '#3b82f6', borderBottom: `1px solid ${colors.borderColor}`,
              }}
            >
              {b.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TemplateParamsEditor = ({
  template,
  value,
  onChange,
  capabilities = CAPS_FULL,
  colors,
  mode = 'bulk',
  contactKey = '',
  contactName = '',
  previewContact = null,
  fields: fieldsProp = null,
}) => {
  const analysis = useMemo(() => analyzeTemplate(template), [template]);
  const v = normalizeValues(value);
  const [fields, setFields] = useState(fieldsProp || []);

  useEffect(() => {
    if (fieldsProp) {
      setFields(fieldsProp);
      return undefined;
    }
    let alive = true;
    loadCustomFields().then((list) => { if (alive) setFields(list); });
    return () => { alive = false; };
  }, [fieldsProp]);

  const update = (patch) => onChange?.({ ...v, ...patch });
  const setBody = (n, raw) => {
    const next = [...v.body_params];
    while (next.length < n) next.push('');
    next[n - 1] = raw;
    update({ body_params: next });
  };
  const setButton = (index, raw) => update({
    button_params: { ...v.button_params, [String(index)]: raw },
  });

  // Prévia com contato real (inbox, telefone digitado, contato de exemplo).
  const previewKey = contactKey || previewContact?.phone || '';
  const previewName = contactName || previewContact?.display_name || previewContact?.name || '';
  const payloadKey = JSON.stringify(buildSendExtra(analysis, v));
  const [remote, setRemote] = useState({ loading: false, data: null });

  useEffect(() => {
    if (!previewKey || looksOpaque(previewKey) || !template?.name || analysis.unsupported) {
      setRemote({ loading: false, data: null });
      return undefined;
    }
    let alive = true;
    setRemote((r) => ({ ...r, loading: true }));
    const timer = setTimeout(() => {
      templateService.preview(template.name, {
        phone: previewKey,
        contact_name: previewName || undefined,
        ...JSON.parse(payloadKey),
      })
        .then((res) => { if (alive) setRemote({ loading: false, data: res?.data || null }); })
        .catch(() => { if (alive) setRemote({ loading: false, data: null }); });
    }, 450);
    return () => { alive = false; clearTimeout(timer); };
  }, [previewKey, previewName, template?.name, payloadKey, analysis.unsupported]);

  const inputStyle = {
    padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    fontSize: 13, outline: 'none', boxSizing: 'border-box', width: '100%',
  };

  if (analysis.unsupported) {
    return (
      <div style={{ display: 'flex', gap: 6, fontSize: 12, color: '#b45309' }}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>{analysis.unsupported}</span>
      </div>
    );
  }

  const header = analysis.header;
  const showHeaderText = header && header.params.length > 0 && capabilities.header;
  const showMedia = header && header.needsMedia && capabilities.media;
  const showButtons = analysis.dynamicButtons.length > 0 && capabilities.buttons;

  // Frase do corpo com cada {{n}} destacado e a escolha dele.
  const sentence = [];
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let last = 0;
  let match;
  const text = analysis.body.text;
  while ((match = re.exec(text))) {
    sentence.push(text.slice(last, match.index));
    const n = parseInt(match[1], 10);
    const label = describeValue(v.body_params[n - 1], fields);
    sentence.push(
      <span
        key={`v${match.index}`}
        style={{
          display: 'inline-block', padding: '0 6px', margin: '0 1px', borderRadius: 6,
          fontWeight: 600, fontSize: 12,
          backgroundColor: `${colors.accentPrimary}1f`, color: colors.accentPrimary,
        }}
      >
        {`{{${n}}}`}{label ? ` ${label}` : ''}
      </span>,
    );
    last = match.index + match[0].length;
  }
  sentence.push(text.slice(last));

  const preview = remote.data || labelPreview(analysis, v);
  const faltasDoContato = mode === 'single' && remote.data
    ? previewMissingSentences(remote.data.missing, analysis)
      .filter((s) => !s.startsWith('Falta'))
    : [];
  const fraseEmMassa = mode !== 'single' ? missingDataSentence(analysis, v) : null;
  const previewTitle = remote.data && previewName
    ? `Prévia para ${previewName}`
    : remote.data ? 'Prévia para este contato' : 'Prévia';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {analysis.body.params.length > 0 && (
        <div style={{
          fontSize: 13, color: colors.textPrimary, whiteSpace: 'pre-wrap', lineHeight: 1.7,
          padding: '8px 10px', borderRadius: 8, border: `1px dashed ${colors.borderColor}`,
        }}>
          {sentence}
        </div>
      )}

      {showMedia && (
        <Row label={`${MEDIA_LABEL[header.format] || 'Mídia'} do cabeçalho`} colors={colors}>
          <MediaField
            format={header.format}
            value={v.header_param}
            onChange={(hp) => update({ header_param: hp })}
            colors={colors}
            inputStyle={inputStyle}
          />
        </Row>
      )}

      {showHeaderText && (
        <Row label="{{1}} no cabeçalho" colors={colors}>
          <ValueSourceField
            raw={v.header_param?.text || ''}
            onChange={(raw) => update({ header_param: { type: 'text', text: raw } })}
            fields={fields}
            colors={colors}
            inputStyle={inputStyle}
            maxLength={60}
          />
        </Row>
      )}

      {capabilities.body && analysis.body.params.map((n) => (
        <Row key={n} label={`${bodyTokenLabel(analysis, n)} no corpo`} colors={colors}>
          <ValueSourceField
            raw={v.body_params[n - 1] || ''}
            onChange={(raw) => setBody(n, raw)}
            fields={fields}
            colors={colors}
            inputStyle={inputStyle}
            maxLength={1024}
          />
        </Row>
      ))}

      {showButtons && analysis.dynamicButtons.map((b) => (
        <Row
          key={b.index}
          label={b.subType === 'copy_code'
            ? `Código do botão "${b.text}"`
            : `Final do link do botão "${b.text}"`}
          colors={colors}
        >
          {b.subType === 'url' && (
            <span style={{ fontSize: 11, color: colors.textTertiary, wordBreak: 'break-all' }}>
              {b.baseUrl}...
            </span>
          )}
          <ValueSourceField
            raw={v.button_params[String(b.index)] || ''}
            onChange={(raw) => setButton(b.index, raw)}
            fields={fields}
            colors={colors}
            inputStyle={inputStyle}
            maxLength={b.subType === 'copy_code' ? 15 : 1024}
          />
        </Row>
      ))}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: colors.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
          {previewTitle}
          {remote.loading && <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
        </span>
        <PreviewBubble preview={preview} colors={colors} />
      </div>

      {faltasDoContato.map((s) => (
        <div key={s} style={{ display: 'flex', gap: 6, fontSize: 12, color: colors.danger || '#ef4444', lineHeight: 1.4 }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{s}</span>
        </div>
      ))}
      {fraseEmMassa && (
        <div style={{ display: 'flex', gap: 6, fontSize: 11.5, color: colors.textTertiary, lineHeight: 1.4 }}>
          <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{fraseEmMassa}</span>
        </div>
      )}
    </div>
  );
};

export default TemplateParamsEditor;
